import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  AuthContextType,
  Permission,
  UserProfile,
  UserRole,
} from '../../../shared/auth';
import { AuthService } from '../services/auth.service';
import { useAuthStore } from '../stores/auth.store';

/**
 * Authentication Context
 *
 * This context provides authentication state and actions to all child components.
 * It acts as a bridge between our Zustand store and React components.
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * This component:
 * - Wraps the app with authentication context
 * - Subscribes to Zustand store changes
 * - Provides authentication state and actions
 * - Handles automatic token refresh
 * - Manages authentication lifecycle
 */
// Internal component that handles the actual store interaction
const AuthProviderInternal: React.FC<AuthProviderProps> = ({ children }) => {
  // Get all state and actions from the store
  const {
    // State
    isAuthenticated,
    user,
    isLoading,
    error,
    isInitialized,

    // Actions
    login,
    logout,
    clearError,
    initialize,
    handlePKCECallback,

    // Utilities
    hasPermission,
    isAdmin,
    hasRole,
    hasAnyRole,
  } = useAuthStore();

  // ===== CONTEXT VALUE =====

  const contextValue: AuthContextType = useMemo(
    () => ({
      // State
      isAuthenticated,
      user,
      isLoading,
      error,
      isInitialized,

      // Actions
      login,
      logout,
      clearError,
      initialize,
      handlePKCECallback,

      // User profile methods (using current user from store)
      getUserProfile: async () => {
        if (!user) return null;
        return await AuthService.getUserProfile(user.id);
      },

      updateUserProfile: async (updates: Partial<UserProfile>) => {
        if (!user) return;
        await AuthService.updateUserProfile(user.id, updates);
      },

      // Permission-based access control
      hasPermission: (permission: Permission) => hasPermission(permission),
      isAdmin: () => isAdmin(),

      // Role-based (backward compat)
      hasRole: (role: UserRole) => hasRole(role),
      hasAnyRole: (roles: UserRole[]) => hasAnyRole(roles),
    }),
    [
      isAuthenticated,
      user,
      isLoading,
      error,
      isInitialized,
      login,
      logout,
      clearError,
      initialize,
      handlePKCECallback,
      hasPermission,
      isAdmin,
      hasRole,
      hasAnyRole,
    ]
  );

  // ===== EFFECTS =====

  /**
   * Initialize authentication on mount
   */
  const initializeAuth = useCallback(async () => {
    if (!isInitialized) {
      await initialize();
    }
  }, [initialize, isInitialized]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Listen for logout redirect failure (redirect to Zitadel never happened)
  useEffect(() => {
    const handler = () => {
      useAuthStore.getState().handleRedirectFailure();
    };
    window.addEventListener('periverse:logout-redirect-failed', handler);
    return () => window.removeEventListener('periverse:logout-redirect-failed', handler);
  }, []);

  // PKCE callback is now handled by the AuthCallbackPage route component
  // No need for duplicate handling here

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

/**
 * Main Authentication Provider Component
 *
 * This is the public component that should be used in your app.
 * It wraps the internal provider with error boundaries and other safety measures.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return <AuthProviderInternal>{children}</AuthProviderInternal>;
};

/**
 * Hook to use authentication context
 *
 * This hook provides access to authentication state and actions.
 * It must be used within an AuthProvider.
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

/**
 * Fallback context value for testing
 */
export const AuthProviderFallback: AuthContextType = {
  // State
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,
  isInitialized: false,

  // Actions
  login: async () => {
    throw new Error('AuthProvider not initialized');
  },
  logout: async () => {
    throw new Error('AuthProvider not initialized');
  },
  clearError: () => {
    throw new Error('AuthProvider not initialized');
  },
  initialize: async () => {
    throw new Error('AuthProvider not initialized');
  },
  handlePKCECallback: async () => {
    throw new Error('AuthProvider not initialized');
  },
  getUserProfile: async () => {
    throw new Error('AuthProvider not initialized');
  },
  updateUserProfile: async () => {
    throw new Error('AuthProvider not initialized');
  },
  hasPermission: () => {
    throw new Error('AuthProvider not initialized');
  },
  isAdmin: () => {
    throw new Error('AuthProvider not initialized');
  },
  hasRole: () => {
    throw new Error('AuthProvider not initialized');
  },
  hasAnyRole: () => {
    throw new Error('AuthProvider not initialized');
  },
};
